import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, ArrowRight, TrendingUp, TrendingDown, Minus, CheckCircle2, AlertTriangle, RefreshCw, GitCompare } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── small helpers ───────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().slice(0, 16).replace("T", " "); } catch { return iso; }
};
const scoreColor = (s) =>
  s == null ? "bg-slate-100 text-slate-400"
  : s < 30  ? "bg-rose-50 text-rose-700"
  : s < 70  ? "bg-amber-50 text-amber-700"
  :           "bg-emerald-50 text-emerald-700";

const DeltaBadge = ({ value, higherIsBetter = false, suffix = "" }) => {
  if (value == null || Number.isNaN(value)) return <span className="text-[10px] text-slate-400 font-mono">—</span>;
  if (value === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-slate-50 text-slate-600">
        <Minus className="w-3 h-3" /> 0{suffix}
      </span>
    );
  }
  const improved = higherIsBetter ? value > 0 : value < 0;
  const Icon = improved ? TrendingUp : TrendingDown;
  return (
    <span className={cn(
      "inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm",
      improved ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
    )}>
      <Icon className="w-3 h-3" />{value > 0 ? "+" : ""}{value}{suffix}
    </span>
  );
};

// ─── main component ──────────────────────────────────────────────────────
export default function MdqCompare() {
  const [runs, setRuns] = useState(null);
  const [baselineId, setBaselineId] = useState("");
  const [currentId, setCurrentId] = useState("");
  const [baseline, setBaseline] = useState(null);
  const [current, setCurrent] = useState(null);
  const [loadingBoth, setLoadingBoth] = useState(false);

  // Load index of runs (sorted desc by date — backend default).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/mdq/runs?limit=100`);
        const list = data?.runs || [];
        setRuns(list);
        // Auto-pick latest as Current and the run before that as Baseline.
        if (list.length >= 2) {
          setCurrentId(list[0].id);
          setBaselineId(list[1].id);
        } else if (list.length === 1) {
          setCurrentId(list[0].id);
        }
      } catch {
        toast.error("Failed to load runs");
        setRuns([]);
      }
    })();
  }, []);

  // Fetch full run details when either pick changes.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!baselineId && !currentId) return;
      setLoadingBoth(true);
      try {
        const [b, c] = await Promise.all([
          baselineId ? axios.get(`${API}/mdq/runs/${baselineId}`).then(r => r.data) : Promise.resolve(null),
          currentId  ? axios.get(`${API}/mdq/runs/${currentId}`).then(r => r.data)  : Promise.resolve(null),
        ]);
        if (!alive) return;
        setBaseline(b);
        setCurrent(c);
      } catch {
        if (alive) toast.error("Failed to load one of the runs");
      } finally {
        if (alive) setLoadingBoth(false);
      }
    })();
    return () => { alive = false; };
  }, [baselineId, currentId]);

  if (runs === null) {
    return (
      <div className="bg-white border border-valantic-border rounded-md p-10 flex items-center justify-center text-valantic-muted gap-2 text-sm">
        <Loader2 className="w-4 h-4 ibp-spin-slow" /> Loading runs…
      </div>
    );
  }

  if (runs.length < 2) {
    return (
      <div className="bg-white border border-valantic-border rounded-md p-10 text-center" data-testid="compare-empty">
        <GitCompare className="w-8 h-8 text-slate-400 mx-auto mb-3" strokeWidth={1.5} />
        <div className="font-display text-base font-medium text-valantic-black">Need at least 2 runs to compare</div>
        <p className="text-sm text-valantic-muted mt-1">
          {runs.length === 0
            ? "Run two MDQ analyses first — then come back to measure improvement after your remediation sprint."
            : "You currently have 1 run on file. Run another analysis (e.g. after applying P0 fixes) to unlock a side-by-side comparison."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="compare-view">
      <RunPickerBar
        runs={runs}
        baselineId={baselineId} currentId={currentId}
        onBaseline={setBaselineId} onCurrent={setCurrentId}
        loading={loadingBoth}
      />

      {!baseline || !current ? (
        <div className="bg-white border border-valantic-border rounded-md p-10 flex items-center justify-center text-valantic-muted gap-2 text-sm">
          <Loader2 className="w-4 h-4 ibp-spin-slow" /> Fetching run details…
        </div>
      ) : baselineId === currentId ? (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-5 text-sm text-amber-900" data-testid="compare-same-run-warning">
          <AlertTriangle className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Baseline and Current are the same run. Pick two different runs to see a delta.
        </div>
      ) : (
        <>
          <KpiDeltaGrid baseline={baseline} current={current} />
          <SeverityBreakdown baseline={baseline} current={current} />
          <GapIdDiff baseline={baseline} current={current} />
          <TopBlockersCompare baseline={baseline} current={current} />
          <KfReadinessCompare baseline={baseline} current={current} />
        </>
      )}
    </div>
  );
}

// ─── Run picker bar ──────────────────────────────────────────────────────
function RunPickerBar({ runs, baselineId, currentId, onBaseline, onCurrent, loading }) {
  const label = (r) => {
    if (!r) return "—";
    const score = r?.summary?.overallScore;
    return `${fmtDate(r.created_at)} · ${r.product} · ${r.industry} · ${score ?? "?"}/100`;
  };
  const swap = () => {
    const tmp = baselineId;
    onBaseline(currentId);
    onCurrent(tmp);
  };

  return (
    <div className="bg-white border border-valantic-border rounded-md p-4">
      <div className="flex flex-col lg:flex-row lg:items-end gap-3">
        <div className="flex-1">
          <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted mb-1">Baseline (older run)</label>
          <select
            data-testid="compare-baseline-select"
            value={baselineId}
            onChange={(e) => onBaseline(e.target.value)}
            className="w-full h-9 px-2 text-xs font-mono bg-white border border-valantic-border rounded-sm focus:outline-none focus:border-valantic-red"
          >
            <option value="">— Pick a baseline —</option>
            {runs.map((r) => (
              <option key={r.id} value={r.id}>{label(r)}</option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={swap}
          disabled={!baselineId || !currentId}
          data-testid="compare-swap-btn"
          title="Swap baseline and current"
          className="self-end h-9 w-9 inline-flex items-center justify-center border border-valantic-border bg-white rounded-sm hover:bg-slate-50 disabled:opacity-40 text-valantic-muted"
        >
          <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>

        <div className="flex-1">
          <label className="block font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted mb-1">Current (newer run)</label>
          <select
            data-testid="compare-current-select"
            value={currentId}
            onChange={(e) => onCurrent(e.target.value)}
            className="w-full h-9 px-2 text-xs font-mono bg-white border border-valantic-border rounded-sm focus:outline-none focus:border-valantic-red"
          >
            <option value="">— Pick a current —</option>
            {runs.map((r) => (
              <option key={r.id} value={r.id}>{label(r)}</option>
            ))}
          </select>
        </div>
      </div>
      {loading && (
        <div className="mt-3 text-[11px] font-mono text-valantic-muted inline-flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 ibp-spin-slow" /> Fetching run details…
        </div>
      )}
    </div>
  );
}

// ─── KPI delta cards (4) ─────────────────────────────────────────────────
function KpiDeltaGrid({ baseline, current }) {
  const a = current?.analysis?.summary || current?.summary || {};
  const b = baseline?.analysis?.summary || baseline?.summary || {};
  const cards = [
    { label: "DQ score",       a: a.overallScore, b: b.overallScore, higherIsBetter: true,  suffix: "" },
    { label: "Critical gaps",  a: a.criticalGaps, b: b.criticalGaps, higherIsBetter: false, suffix: "" },
    { label: "High gaps",      a: a.highGaps,     b: b.highGaps,     higherIsBetter: false, suffix: "" },
    { label: "Medium gaps",    a: a.mediumGaps,   b: b.mediumGaps,   higherIsBetter: false, suffix: "" },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="compare-kpi-grid">
      {cards.map((c) => {
        const delta = (c.a ?? 0) - (c.b ?? 0);
        return (
          <div key={c.label} className="bg-white border border-valantic-border rounded-md p-4" data-testid={`compare-kpi-${c.label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted mb-2">{c.label}</div>
            <div className="flex items-center gap-2">
              <span className={cn("w-9 h-9 rounded-sm inline-flex items-center justify-center font-display font-bold text-xs", scoreColor(c.label === "DQ score" ? c.b : 100 - (c.b ?? 0)))}>{c.b ?? "—"}</span>
              <ArrowRight className="w-3 h-3 text-valantic-silver" />
              <span className={cn("w-9 h-9 rounded-sm inline-flex items-center justify-center font-display font-bold text-xs", scoreColor(c.label === "DQ score" ? c.a : 100 - (c.a ?? 0)))}>{c.a ?? "—"}</span>
              <DeltaBadge value={delta} higherIsBetter={c.higherIsBetter} suffix={c.suffix} />
            </div>
            <div className="mt-2 text-[10px] text-valantic-muted font-mono">
              {fmtDate(baseline.created_at)} → {fmtDate(current.created_at)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Severity breakdown side-by-side ─────────────────────────────────────
function SeverityBreakdown({ baseline, current }) {
  const categories = ["masterDataGaps", "transactionDataGaps", "plausibilityChecks", "erpConsistencyChecks", "deadObjects"];
  const labels = {
    masterDataGaps: "Master Data Gaps",
    transactionDataGaps: "Transaction Data Gaps",
    plausibilityChecks: "Plausibility Checks",
    erpConsistencyChecks: "ERP ↔ IBP Consistency",
    deadObjects: "Dead Objects",
  };

  const countSev = (analysis, key, sev) =>
    (analysis?.[key] || []).filter((g) => (g.sev || "").toLowerCase() === sev).length;

  const a = current?.analysis || {};
  const b = baseline?.analysis || {};

  return (
    <div className="bg-white border border-valantic-border rounded-md overflow-hidden" data-testid="compare-severity-table">
      <div className="px-4 py-3 border-b border-valantic-border bg-valantic-bg">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted">Issue category × severity</div>
        <div className="font-display text-base font-bold text-valantic-black">Where did the gaps move?</div>
      </div>
      <div className="overflow-x-auto ibp-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-valantic-bg/50 border-b border-valantic-border">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">Category</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">Critical (B → C)</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">High (B → C)</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">Medium (B → C)</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">Total Δ</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => {
              const bC = countSev(b, cat, "critical");
              const aC = countSev(a, cat, "critical");
              const bH = countSev(b, cat, "high");
              const aH = countSev(a, cat, "high");
              const bM = countSev(b, cat, "medium");
              const aM = countSev(a, cat, "medium");
              const totalB = bC + bH + bM;
              const totalA = aC + aH + aM;
              const totalD = totalA - totalB;
              if (totalA === 0 && totalB === 0) return null;
              return (
                <tr key={cat} className="border-b border-valantic-border last:border-0 hover:bg-slate-50/60" data-testid={`compare-row-${cat}`}>
                  <td className="px-3 py-2 text-xs font-medium text-valantic-black">{labels[cat]}</td>
                  <td className="px-3 py-2 text-center text-xs font-mono">{bC} <ArrowRight className="w-3 h-3 inline text-valantic-silver mx-1" /> {aC} <DeltaBadge value={aC - bC} /></td>
                  <td className="px-3 py-2 text-center text-xs font-mono">{bH} <ArrowRight className="w-3 h-3 inline text-valantic-silver mx-1" /> {aH} <DeltaBadge value={aH - bH} /></td>
                  <td className="px-3 py-2 text-center text-xs font-mono">{bM} <ArrowRight className="w-3 h-3 inline text-valantic-silver mx-1" /> {aM} <DeltaBadge value={aM - bM} /></td>
                  <td className="px-3 py-2 text-center text-xs font-mono">
                    <span className="font-bold">{totalB} <ArrowRight className="w-3 h-3 inline text-valantic-silver mx-1" /> {totalA}</span>{" "}
                    <DeltaBadge value={totalD} />
                  </td>
                </tr>
              );
            })}
            {categories.every((cat) => (a?.[cat] || []).length === 0 && (b?.[cat] || []).length === 0) && (
              <tr><td colSpan="5" className="px-3 py-6 text-center text-xs text-valantic-muted">Neither run reported any gaps in these categories.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Resolved / Persistent / New gap-ID diff ─────────────────────────────
function GapDiffCard({ title, items, color, icon: Icon, tone, testid }) {
  return (
    <div className={cn("bg-white border rounded-md p-4 h-full", color)} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={cn("w-4 h-4", tone)} strokeWidth={1.75} />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted">{title}</div>
          <div className="font-display text-lg font-bold text-valantic-black leading-tight">{items.length}</div>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-valantic-muted">No items in this bucket.</div>
      ) : (
        <ul className="space-y-1 max-h-44 overflow-auto ibp-scroll">
          {items.slice(0, 8).map((g, i) => (
            <li key={i} className="text-[11px] font-mono text-slate-700 flex items-center gap-1.5">
              <span className={cn("inline-block w-1.5 h-1.5 rounded-full", tone.replace("text-", "bg-"))} />
              <span className="text-valantic-muted">{g.obj || "?"}</span>
              <span className="text-slate-400">·</span>
              <span className="font-semibold">{g.attr || "?"}</span>
              {typeof g.pct === "number" && (
                <span className="text-[10px] text-valantic-muted">({g.pct}%)</span>
              )}
              {g._prev && typeof g._prev.pct === "number" && typeof g.pct === "number" && (
                <span className="ml-auto text-[10px] font-mono">
                  {g._prev.pct}% → {g.pct}%
                  {g._prev.pct !== g.pct && (
                    <span className={cn("ml-1 font-bold", g.pct < g._prev.pct ? "text-emerald-700" : "text-rose-700")}>
                      ({g.pct < g._prev.pct ? "↓" : "↑"}{Math.abs(g.pct - g._prev.pct)})
                    </span>
                  )}
                </span>
              )}
            </li>
          ))}
          {items.length > 8 && (
            <li className="text-[10px] text-valantic-muted">+{items.length - 8} more…</li>
          )}
        </ul>
      )}
    </div>
  );
}

function GapIdDiff({ baseline, current }) {
  const bGaps = baseline?.analysis?.masterDataGaps || [];
  const aGaps = current?.analysis?.masterDataGaps || [];

  // Use a composite key for matching: prefer attr+obj+s4Tab since gap IDs are reassigned per run.
  const keyOf = (g) => `${(g.obj || g.s4Tab || "").toUpperCase()}|${(g.attr || g.s4Fld || "").toUpperCase()}`;
  const bMap = new Map(bGaps.map((g) => [keyOf(g), g]));
  const aMap = new Map(aGaps.map((g) => [keyOf(g), g]));

  const resolved = [...bMap.entries()].filter(([k]) => !aMap.has(k)).map(([, g]) => g);
  const persistent = [...aMap.entries()].filter(([k]) => bMap.has(k)).map(([k, g]) => ({ ...g, _prev: bMap.get(k) }));
  const fresh = [...aMap.entries()].filter(([k]) => !bMap.has(k)).map(([, g]) => g);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="compare-gap-diff">
      <GapDiffCard
        title="Resolved (in baseline, gone in current)"
        items={resolved}
        color="border-emerald-200"
        icon={CheckCircle2}
        tone="text-emerald-600"
        testid="compare-gap-diff-resolved"
      />
      <GapDiffCard
        title="Persistent (in both)"
        items={persistent}
        color="border-amber-200"
        icon={AlertTriangle}
        tone="text-amber-600"
        testid="compare-gap-diff-persistent"
      />
      <GapDiffCard
        title="New (only in current)"
        items={fresh}
        color="border-rose-200"
        icon={AlertTriangle}
        tone="text-rose-600"
        testid="compare-gap-diff-new"
      />
    </div>
  );
}

// ─── Top blockers side-by-side ───────────────────────────────────────────
function TopBlockersCompare({ baseline, current }) {
  const bList = baseline?.analysis?.summary?.top3Blockers || baseline?.summary?.top3Blockers || [];
  const aList = current?.analysis?.summary?.top3Blockers  || current?.summary?.top3Blockers  || [];
  const max = Math.max(bList.length, aList.length, 1);
  return (
    <div className="bg-white border border-valantic-border rounded-md overflow-hidden" data-testid="compare-top-blockers">
      <div className="px-4 py-3 border-b border-valantic-border bg-valantic-bg">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted">Top blockers</div>
        <div className="font-display text-base font-bold text-valantic-black">Baseline vs Current</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-valantic-border">
        {[{ list: bList, label: "Baseline", testid: "blockers-baseline" }, { list: aList, label: "Current", testid: "blockers-current" }].map((col) => (
          <div key={col.label} className="p-4" data-testid={col.testid}>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted mb-2">{col.label}</div>
            {col.list.length ? (
              <ol className="space-y-1.5 list-decimal list-inside text-xs text-slate-800">
                {Array.from({ length: max }).map((_, i) => (
                  <li key={i} className={col.list[i] ? "" : "list-none text-valantic-muted/40 italic"}>
                    {col.list[i] || "(empty slot)"}
                  </li>
                ))}
              </ol>
            ) : (
              <div className="text-[11px] text-valantic-muted">No blockers reported.</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── KF readiness side-by-side ───────────────────────────────────────────
function KfReadinessCompare({ baseline, current }) {
  const a = current?.analysis?.kpiReadiness || {};
  const b = baseline?.analysis?.kpiReadiness || {};
  const keys = useMemo(() => {
    const s = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...s];
  }, [a, b]);

  if (keys.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border border-valantic-border rounded-md overflow-hidden" data-testid="compare-kf-readiness">
      <div className="px-4 py-3 border-b border-valantic-border bg-valantic-bg">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-valantic-muted">Key figure readiness</div>
        <div className="font-display text-base font-bold text-valantic-black">Per-KF readiness % delta</div>
      </div>
      <div className="overflow-x-auto ibp-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-valantic-bg/50 border-b border-valantic-border">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">Key figure</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">Baseline %</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">Current %</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase tracking-widest text-valantic-muted font-medium">Δ</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => {
              const bp = b[k]?.pct ?? null;
              const ap = a[k]?.pct ?? null;
              const delta = (ap ?? 0) - (bp ?? 0);
              return (
                <tr key={k} className="border-b border-valantic-border last:border-0 hover:bg-slate-50/60" data-testid={`compare-kf-${k}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-valantic-black">{k}</td>
                  <td className="px-3 py-2 text-center text-xs font-mono">{bp ?? "—"}</td>
                  <td className="px-3 py-2 text-center text-xs font-mono">{ap ?? "—"}</td>
                  <td className="px-3 py-2 text-center"><DeltaBadge value={delta} higherIsBetter={true} suffix="%" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
